import { useEffect, useState } from 'react';
import { GuidedField } from '../../guidance/GuidedField';
import { fieldDef } from '../../guidance/roleFieldRegistry';
import { jobRoleGateway } from '../../index';
import { jobRoleErrorMessage } from '../../jobRoleErrors';
import type { JobRoleListItem, JobRoleTemplateDetail, OccupationItem, OccupationSystem } from '../../types';
import { GroupedCombobox, type ComboLoadResult } from '../guided/GroupedCombobox';
import { DepartmentField } from '../guided/DepartmentField';
import { OccupationLookup } from '../guided/OccupationLookup';
import { SuggestionBox } from '../guided/SuggestionBox';
import { ApplyChips, type ApplyChip } from './ApplyChips';
import { LinesField, ReferenceComboField, ReferenceSelectField, TextAreaField } from './fields';
import { ROLE_CODE_FORMAT } from '../../roleForm';
import { leadingSentences } from './text';
import { useWizard } from './WizardContext';

const SUMMARY_PROMPTS = [
  'Why does this role exist?',
  'What is it primarily accountable for?',
  'Who does it support?',
  'What outcomes show it is going well?',
];

/** Similar existing role titles, found through the role list search. The links open in a new tab so the wizard is never left. */
function SimilarRoles() {
  const w = useWizard();
  const title = w.form.title.trim();
  const [similar, setSimilar] = useState<JobRoleListItem[]>([]);
  useEffect(() => {
    if (title.length < 3) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setSimilar([]);
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      jobRoleGateway
        .listRoles({ search: title, pageSize: 5 }, controller.signal)
        .then((page) => { if (!controller.signal.aborted) setSimilar(page.items.filter((r) => r.id !== w.roleId)); })
        .catch(() => { if (!controller.signal.aborted) setSimilar([]); });
    }, 500);
    return () => { window.clearTimeout(timer); controller.abort(); };
  }, [title, w.roleId]);
  if (similar.length === 0) return null;
  const exact = similar.some((r) => r.title.toLowerCase() === title.toLowerCase());
  return (
    <div className="jr-warning" data-testid="similar-roles">
      <p>
        <span aria-hidden="true">{'⚠'} </span>
        {exact ? 'A role with this title already exists in your company.' : 'Roles with a similar title already exist in your company.'}
        {' '}Check that you are not adding the same role twice.
      </p>
      <ul className="jr-plain-list">
        {similar.map((r) => (
          <li key={r.id}>
            <a href={`/people/roles/${r.id}`} target="_blank" rel="noopener noreferrer">{r.title}{' '}<span className="jr-sr-only">(opens in a new tab)</span></a>
            {r.departmentName ? <span className="muted"> {r.departmentName}</span> : null}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function DetailsStep() {
  const w = useWizard();
  const f = w.form;
  const [titleTemplate, setTitleTemplate] = useState<JobRoleTemplateDetail | null>(null);
  const [chips, setChips] = useState<{ source: string; items: ApplyChip[] } | null>(null);
  const [applied, setApplied] = useState('');
  const [codeBusy, setCodeBusy] = useState(false);
  const [codeSuggestion, setCodeSuggestion] = useState<string | null>(null);
  const [codeStatus, setCodeStatus] = useState<{ available: boolean | null; error: string | null }>({ available: null, error: null });
  const [codeNote, setCodeNote] = useState('');
  const effectiveTemplate = titleTemplate
    ? { id: titleTemplate.id, title: titleTemplate.title, content: titleTemplate.content }
    : w.suggestionTemplate;

  // ---- title suggestions: templates and official occupation titles. Picking one only fills the title; the rest come as chips. ----
  async function loadTitles(query: string, signal: AbortSignal): Promise<ComboLoadResult> {
    if (query.length < 2) return { groups: [] };
    const [templates, occupations] = await Promise.all([
      jobRoleGateway.listTemplates(query, signal).catch(() => []),
      jobRoleGateway.searchOccupations({ system: 'Onet', search: query, take: 6 }, signal).catch(() => null),
    ]);
    const groups: ComboLoadResult['groups'] = [];
    if (templates.length > 0) {
      groups.push({
        key: 'templates', label: 'EPN templates',
        options: templates.slice(0, 5).map((t) => ({ value: `tpl:${t.id}`, label: t.title, detail: [t.socCode ? `SOC ${t.socCode}` : null, t.jobFamily].filter(Boolean).join(' · ') || null })),
      });
    }
    const seen = new Set<string>();
    const occ = (occupations?.items ?? []).map((o) => ({ o, label: o.matchedOn === 'Synonym' && o.matchedText ? o.matchedText : o.title })).filter(({ label }) => {
      const k = label.toLowerCase();
      if (seen.has(k) || templates.some((t) => t.title.toLowerCase() === k)) return false;
      seen.add(k);
      return true;
    });
    if (occ.length > 0) {
      groups.push({
        key: 'occupations', label: 'Official occupation titles (recommended matches)',
        options: occ.map(({ o, label }) => ({ value: `occ:${o.code}`, label, detail: `O*NET ${o.code} ${o.title}` })),
      });
    }
    return { groups };
  }

  async function titlePicked(value: string) {
    setApplied('');
    try {
      if (value.startsWith('tpl:')) {
        const detail = await jobRoleGateway.getTemplate(value.slice(4));
        setTitleTemplate(detail);
        setChips({ source: `the ${detail.title} template`, items: await templateChips(detail) });
      } else if (value.startsWith('occ:')) {
        const item = await jobRoleGateway.getOccupation('Onet', value.slice(4));
        setTitleTemplate(null);
        setChips({ source: 'the official occupation list', items: occupationChips(item) });
      }
    } catch {
      /* the title is already in the field; suggestions are a convenience */
    }
  }

  function applyOccupation(system: OccupationSystem, item: OccupationItem) {
    if (system === 'Soc') w.setMany({ socCode: item.code, socStatus: 'Confirmed', socNotYetClassifiedReason: '' });
    else w.setMany({ onetCode: item.code, onetStatus: 'Confirmed', onetNotYetClassifiedReason: '' });
    w.setOccupation(system, item);
    setApplied(`${system === 'Soc' ? 'SOC' : 'O*NET'} code ${item.code} applied.`);
  }

  function occupationChips(item: OccupationItem): ApplyChip[] {
    const out: ApplyChip[] = [];
    if (item.socCode) {
      const code = item.socCode;
      out.push({
        id: 'soc', label: 'SOC code', display: code, current: f.socCode,
        onApply: async () => applyOccupation('Soc', await jobRoleGateway.getOccupation('Soc', code)),
      });
    }
    out.push({ id: 'onet', label: 'O*NET code', display: item.code, current: f.onetCode, onApply: () => applyOccupation('Onet', item) });
    return out;
  }

  async function templateChips(t: JobRoleTemplateDetail): Promise<ApplyChip[]> {
    const out: ApplyChip[] = [];
    if (t.jobFamily) out.push({ id: 'family', label: 'Job family', display: t.jobFamily, current: f.jobFamily, onApply: () => { w.set('jobFamily', t.jobFamily!); setApplied(`Job family ${t.jobFamily} applied.`); } });
    if (t.careerLevel) out.push({ id: 'level', label: 'Level', display: t.careerLevel, current: f.careerLevel, onApply: () => { w.set('careerLevel', t.careerLevel!); setApplied(`Level ${t.careerLevel} applied.`); } });
    if (t.suggestedDepartmentName) {
      const name = t.suggestedDepartmentName;
      const existing = w.departments.find((d) => d.isActive && d.name.toLowerCase() === name.toLowerCase());
      const current = w.departments.find((d) => d.id === f.departmentId)?.name ?? '';
      if (existing) {
        out.push({ id: 'dept', label: 'Department', display: existing.name, current, onApply: () => { w.set('departmentId', existing.id); setApplied(`Department ${existing.name} applied.`); } });
      } else if (w.caps?.canCreateDepartment !== false) {
        out.push({ id: 'dept', label: 'Department (not in your company yet)', display: name, current: '', actionLabel: 'Create department…', noConfirm: true, onApply: () => w.requestAddDepartment(name) });
      }
    }
    if (t.socCode) {
      const code = t.socCode;
      out.push({
        id: 'soc', label: 'SOC code', display: code, current: f.socCode,
        onApply: async () => applyOccupation('Soc', await jobRoleGateway.getOccupation('Soc', code)),
      });
    }
    if (t.onetCode) {
      const code = t.onetCode;
      out.push({
        id: 'onet', label: 'O*NET code', display: code, current: f.onetCode,
        onApply: async () => applyOccupation('Onet', await jobRoleGateway.getOccupation('Onet', code)),
      });
    }
    return out;
  }

  // ---- the internal code: a suggestion on request, an availability check as it is typed, never regenerated for the person ----
  const codeParams = (proposed?: string) => ({
    title: f.title.trim() || undefined, departmentId: f.departmentId || undefined, jobFamily: f.jobFamily.trim() || undefined,
    careerLevel: f.careerLevel.trim() || undefined, ...(proposed ? { proposed } : {}), ...(w.roleId ? { excludeRoleId: w.roleId } : {}),
  });

  useEffect(() => {
    const proposed = f.roleCode.trim();
    // An invalid code is a 400 on the server (also when only checking), so it is not asked about: the format message is the answer.
    if (!proposed || !ROLE_CODE_FORMAT.test(proposed) || proposed.length > 40) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCodeStatus({ available: null, error: null });
      return;
    }
    const controller = new AbortController();
    const timer = window.setTimeout(() => {
      jobRoleGateway
        .suggestRoleCode(codeParams(proposed), controller.signal)
        .then((r) => { if (!controller.signal.aborted) setCodeStatus({ available: r.proposedAvailable, error: null }); })
        .catch(() => { if (!controller.signal.aborted) setCodeStatus({ available: null, error: null }); });
    }, 600);
    return () => { window.clearTimeout(timer); controller.abort(); };
    // The check runs when the typed code (or what the code is judged against) changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.roleCode, w.roleId]);

  async function suggestCode() {
    setCodeBusy(true);
    setCodeSuggestion(null);
    setCodeNote('');
    try {
      const r = await jobRoleGateway.suggestRoleCode(codeParams());
      if (!f.roleCode.trim()) {
        w.set('roleCode', r.suggestion);
        setCodeNote(`Suggested code ${r.suggestion} was put in the field. You can change it.`);
      } else if (f.roleCode.trim() === r.suggestion) {
        setCodeNote('The code you wrote is the one EPN would suggest.');
      } else {
        setCodeSuggestion(r.suggestion); // the field has text: it is shown beside the field, never written into it
      }
    } catch (err) {
      setCodeNote(jobRoleErrorMessage(err, 'Could not suggest a code.'));
    } finally {
      setCodeBusy(false);
    }
  }

  const departmentName = w.departments.find((d) => d.id === f.departmentId)?.name ?? '';

  return (
    <div className="jr-step jr-grid" data-testid="details-step">
      <div className="jr-grid__wide">
        <GuidedField field={fieldDef('title')} error={w.errorOf('title')} counter={{ length: f.title.length, max: 150 }}>
          {(a) => (
            <GroupedCombobox
              a11y={a}
              mode="text"
              value={f.title}
              disabled={w.disabled}
              maxLength={200}
              load={loadTitles}
              onTextChange={(text) => w.set('title', text)}
              onSelect={(o) => { void titlePicked(o.value); }}
              placeholder="For example the name people use for the job"
            />
          )}
        </GuidedField>
        <SimilarRoles />
        {chips && (
          <>
            <ApplyChips source={chips.source} chips={chips.items} onDismiss={() => setChips(null)} />
            <p className="jr-sr-only" role="status" aria-live="polite">{applied}</p>
          </>
        )}
      </div>

      <LinesField k="alternateTitles" rows={2} isWide />

      <TextAreaField k="description" rows={2} isWide>
        {effectiveTemplate?.content.summary && (
          <SuggestionBox current={f.description} suggestion={leadingSentences(effectiveTemplate.content.summary, 250)} source={`the ${effectiveTemplate.title} template`} noun="description" onUse={(t) => w.set('description', t)} />
        )}
      </TextAreaField>

      <GuidedField
        field={fieldDef('roleCode')} error={w.errorOf('roleCode')}
        hint="Your own label for the role, for example FIN-PS2: capital letters, numbers and dashes work well. It is not a SOC code, not a payroll code and not a system access role: it grants nothing."
        headExtra={(
          <button type="button" className="jr-btn jr-btn--secondary jr-btn--small" disabled={w.disabled || codeBusy} onClick={() => void suggestCode()}>
            {codeBusy ? 'Suggesting…' : 'Suggest a code'}
          </button>
        )}
      >
        {(a) => (
          <>
            <input {...a} type="text" autoComplete="off" maxLength={40} value={f.roleCode} disabled={w.disabled} onChange={(e) => w.set('roleCode', e.target.value)} />
            <p className="jr-hint" role="status" aria-live="polite" data-testid="role-code-status">
              {codeStatus.available === false
                ? 'That code is already used by another role in your company. Codes are not case sensitive. Choose another, or suggest one.'
                : codeStatus.available === true ? 'That code is available.' : ''}
              {codeNote ? ` ${codeNote}` : ''}
            </p>
            {codeSuggestion && (
              <SuggestionBox current={f.roleCode} suggestion={codeSuggestion} source="your department, title and level" noun="code" onUse={(t) => { w.set('roleCode', t); setCodeSuggestion(null); }} />
            )}
          </>
        )}
      </GuidedField>

      <GuidedField field={fieldDef('departmentId')} error={w.errorOf('departmentId')}>
        {(a) => (
          <DepartmentField
            ref={w.departmentRef}
            a11y={a}
            value={f.departmentId}
            departments={w.departments}
            canCreate={w.caps ? w.caps.canCreateDepartment : null}
            templateId={effectiveTemplate?.id ?? null}
            disabled={w.disabled}
            onSelect={(id) => w.set('departmentId', id)}
            onRequestAdd={w.requestAddDepartment}
          />
        )}
      </GuidedField>
      {w.newDepartmentIds.includes(f.departmentId) && departmentName && (
        <p className="jr-hint jr-grid__wide" data-testid="new-department-note">{departmentName} is a new department, created in this session. It is saved for your whole company.</p>
      )}

      <ReferenceComboField k="jobFamily" />
      <ReferenceSelectField k="careerLevel" />
      <ReferenceComboField k="managementLevel" />

      <div className="jr-grid__wide">
        <GuidedField field={fieldDef('socCode')} error={w.errorOf('socCode')}>
          {(a) => (
            <OccupationLookup
              system="Soc" a11y={a} code={f.socCode} status={f.socStatus as '' | 'Confirmed' | 'NotYetClassified'} titleHint={f.title} disabled={w.disabled}
              onPick={(item) => applyOccupation('Soc', item)}
              onClear={() => { w.setMany({ socCode: '', socStatus: '', socNotYetClassifiedReason: '' }); w.setOccupation('Soc', null); }}
              onStatusChange={(s) => w.setMany(s === 'NotYetClassified' ? { socStatus: s, socCode: '' } : { socStatus: s, socNotYetClassifiedReason: '' })}
              onResolve={(item) => w.setOccupation('Soc', item)}
            />
          )}
        </GuidedField>
        {f.socStatus === 'NotYetClassified' && (
          <GuidedField field={fieldDef('socNotYetClassifiedReason')} error={w.errorOf('socNotYetClassifiedReason')} counter={{ length: f.socNotYetClassifiedReason.length, max: 300 }}>
            {(a) => <textarea {...a} rows={2} value={f.socNotYetClassifiedReason} disabled={w.disabled} onChange={(e) => w.set('socNotYetClassifiedReason', e.target.value)} />}
          </GuidedField>
        )}
      </div>

      <div className="jr-grid__wide">
        <GuidedField field={fieldDef('onetCode')} error={w.errorOf('onetCode')}>
          {(a) => (
            <OccupationLookup
              system="Onet" a11y={a} code={f.onetCode} status={f.onetStatus as '' | 'Confirmed' | 'NotYetClassified'} titleHint={f.title} disabled={w.disabled}
              {...(f.socCode ? { socCode: f.socCode } : {})}
              onPick={(item) => applyOccupation('Onet', item)}
              onClear={() => { w.setMany({ onetCode: '', onetStatus: '', onetNotYetClassifiedReason: '' }); w.setOccupation('Onet', null); }}
              onStatusChange={(s) => w.setMany(s === 'NotYetClassified' ? { onetStatus: s, onetCode: '' } : { onetStatus: s, onetNotYetClassifiedReason: '' })}
              onResolve={(item) => w.setOccupation('Onet', item)}
            />
          )}
        </GuidedField>
        {f.onetStatus === 'NotYetClassified' && (
          <GuidedField field={fieldDef('onetNotYetClassifiedReason')} error={w.errorOf('onetNotYetClassifiedReason')} counter={{ length: f.onetNotYetClassifiedReason.length, max: 300 }}>
            {(a) => <textarea {...a} rows={2} value={f.onetNotYetClassifiedReason} disabled={w.disabled} onChange={(e) => w.set('onetNotYetClassifiedReason', e.target.value)} />}
          </GuidedField>
        )}
      </div>

      <div className="jr-grid__wide">
        <GuidedField
          field={fieldDef('summary')} error={w.errorOf('summary')} counter={{ length: f.summary.length, max: 4000 }}
          hint={(
            <>
              Things a good summary answers:{' '}
              {SUMMARY_PROMPTS.map((p, i) => <span key={p}>{i > 0 ? ' ' : ''}{p}</span>)}
            </>
          )}
        >
          {(a) => <textarea {...a} rows={4} value={f.summary} disabled={w.disabled} onChange={(e) => w.set('summary', e.target.value)} />}
        </GuidedField>
        {effectiveTemplate?.content.summary && (
          <SuggestionBox current={f.summary} suggestion={effectiveTemplate.content.summary} source={`the ${effectiveTemplate.title} template`} noun="summary" onUse={(t) => w.set('summary', t)} />
        )}
      </div>

      <TextAreaField k="purpose" rows={2} isWide>
        {effectiveTemplate?.content.purpose && (
          <SuggestionBox current={f.purpose} suggestion={effectiveTemplate.content.purpose} source={`the ${effectiveTemplate.title} template`} noun="purpose" onUse={(t) => w.set('purpose', t)} />
        )}
      </TextAreaField>
    </div>
  );
}
